// Canvas 3: Use Case Prioritisation (workbook tab 03 Use Cases, with a back page of anchors that becomes reference blocks here).
// ONE score per criterion per use case: Value and Feasibility for all candidates (Step 1); Scalability plus the three screens for the
// top three (Step 3). The parts are prompts with 5, 3 and 1 anchors; the rule is "score the criterion as its weakest part".
// Rows: 'uc', 'plot' and 'fin' take the candidates of Canvas 2 (same row ids r1 to r5). 'test' takes the top three, worked out here.
import { makeDef, G, text, pick, calc, fields, table, pull, note, reference } from './base.js';
import { OPT } from '../spine.js';
import { has, S, N, T, toNum, gap } from '../helpers.js';

const LEVELS = ['Ad hoc', 'Emerging', 'Defined', 'Managed', 'Optimised'];

// ---------------------------------------------------------------- the printed back page (anchors, screens, keys), word for word
const VALUE_ANCH = [
  [
    'Customer impact (experience quality)',
    'Fixes a pain customers raise most, and your data shows it',
    'A real pain, but secondary, or heard about and not measured',
    'Customers would not notice',
    'Call reasons, complaint themes, effort score',
  ],
  [
    'Business gain (revenue, cost, productivity)',
    'Moves a KPI your sponsor owns, with a number you can defend',
    'A plausible link to a KPI, not yet sized',
    'No link to any KPI',
    'Case Card C (KPIs) and D (baselines)',
  ],
  [
    'Strategic fit (enterprise priorities)',
    "Named in this year's strategy or board priorities",
    'Fits a stated theme',
    'Unrelated to current priorities',
    'Strategy paper or board pack',
  ],
];
const FEAS_ANCH = [
  [
    'Data (availability and quality)',
    'In one place, a named owner, quality already checked',
    'In several systems, quality unchecked',
    'Does not exist or cannot be reached',
    "The data owner's name; a sample check",
  ],
  [
    'Technology (integration readiness)',
    'Runs on systems and interfaces you already have',
    'Needs one new integration or a vendor',
    'Needs a platform you do not have',
    'The view of IT and Data',
  ],
  [
    'Delivery (complexity, time to value)',
    'Live in about 3 months with one team',
    '6 to 9 months, two or three teams',
    'Over 12 months, or many teams',
    "The delivery lead's estimate",
  ],
];
const SCAL_ANCH = [
  [
    'Reach (across journeys or segments)',
    'The same data and model serve three or more journeys or segments',
    'Extends to one more journey with rework',
    'A one-off for this journey',
  ],
  [
    'Change (operational and change impact)',
    "Fits today's processes; frontline work barely changes",
    'Some roles and processes change',
    'Needs a new operating model',
  ],
];
const SCREENS = [
  [
    'Risk',
    'Low: no sensitive data and no decision about an individual',
    'Medium: sensitive data, or a decision that a person checks',
    "High: an automated decision about a customer's money, access or eligibility",
  ],
  [
    'Trust (at the trust moments on Canvas 2)',
    'plus: the customer can see why and can reach a person',
    'zero: no trust moment is touched',
    'minus: the customer cannot reach a person, or cannot tell how it was decided',
  ],
  [
    'Human',
    'plus: people handle harder cases with better support',
    "zero: people's work does not change",
    'minus: it removes judgement people are paid for, or their ability to help the customer',
  ],
];
const QUADS = [
  ['Quick Win', 'Value 4 or 5, Feasibility 4 or 5'],
  ['Strategic Bet', 'Value 4 or 5, Feasibility 1 to 3'],
  ['Fill-in', 'Value 1 to 3, Feasibility 4 or 5'],
  ['Reconsider', 'Value 1 to 3, Feasibility 1 to 3'],
];
const FLAG_KEY = 'Guardrail flag: Risk High, or Trust minus, or Human minus. It travels with the use case to Canvases 4 and 6.';
const TAG_KEY = [
  ['M', 'Measured: from a report or system you can show.'],
  ['E', 'Estimated: informed, with a stated basis.'],
  ['G', 'Guess: no basis yet.'],
];

// ---------------------------------------------------------------- local helpers
const lc = (s) => s.charAt(0).toLowerCase() + s.slice(1);
const partName = (s) => s.split(' (')[0];
// "Customer impact. 5: fixes a pain... 3: a real pain... 1: customers would not notice."
const anchorRule = (list) => list.map(([part, a5, a3, a1]) => `${partName(part)}. 5: ${lc(a5)}. 3: ${lc(a3)}. 1: ${lc(a1)}.`).join(' ');
const screenRule = (i) => `${SCREENS[i][1]}. ${SCREENS[i][2]}. ${SCREENS[i][3]}.`;
const J = (...parts) => parts.join(' ');

const SCORE_OPTS = [
  { v: '1', label: '1', hint: 'Anchor 1' },
  { v: '2', label: '2', hint: 'Between 1 and 3' },
  { v: '3', label: '3', hint: 'Anchor 3' },
  { v: '4', label: '4', hint: 'Between 3 and 5' },
  { v: '5', label: '5', hint: 'Anchor 5' },
];

const candsOf = (up) => (up && up[2] && up[2].d && up[2].d.facts && up[2].d.facts.candidates) || [];
const cardOf = (up) => (up && up[0] && up[0].d && up[0].d.facts) || {};
const levelOf = (up) => {
  const f = up && up[1] && up[1].d && up[1].d.facts;
  const n = toNum(f && f.dims && f.dims.data && f.dims.data.level);
  return n === null ? null : n;
};
const levelText = (up) => {
  const n = levelOf(up);
  return n && LEVELS[n - 1] ? `Level ${n}, ${LEVELS[n - 1]}` : null;
};

// the printed canvas's and the workbook's exact thresholds: High is 4 or 5
const quadrantOf = (value, feas) => {
  if (value >= 4 && feas >= 4) return 'Quick Win';
  if (value >= 4) return 'Strategic Bet';
  if (feas >= 4) return 'Fill-in';
  return 'Reconsider';
};

// the display line of a candidate, for the row sub-labels
const subOf = (c) => [c.stage, c.type, c.idea ? 'your Case Card idea' : ''].filter(Boolean).join(', ');

// Everything this canvas works out. Pure; never throws on blank or half-filled answers.
// Ranking follows the workbook: V + F, then the higher Value, then the lower use case number.
// The top three, and then the picks, are named only once every candidate (and then every top three row) has its scores.
function analyse(v, up) {
  const ans = v || {};
  const level = levelOf(up);
  const rows = candsOf(up).map((c, i) => {
    const n = c.n || i + 1;
    const value = N(ans, `uc.${c.id}.value`);
    const feas = N(ans, `uc.${c.id}.feas`);
    const vf = value !== null && feas !== null ? value + feas : null;
    return {
      id: c.id,
      n,
      name: c.name || '',
      stage: c.stage || '',
      type: c.type || '',
      idea: !!c.idea,
      value,
      feas,
      vf,
      vtag: S(ans, `uc.${c.id}.vtag`),
      quad: vf === null ? '' : quadrantOf(value, feas),
      key: vf === null ? null : vf * 1000 + value * 100 - n,
      rank: null,
      top3: false,
      scal: null,
      risk: '',
      trust: '',
      human: '',
      reasons: [],
      flag: false,
      flagText: '',
      total: null,
      pick: '',
      data: S(ans, `fin.${c.id}.data`),
      weak: S(ans, `fin.${c.id}.weak`),
      needs: false,
      need: '',
    };
  });

  // Step 2: rank and the top three
  const complete = rows.length > 0 && rows.every((r) => r.key !== null);
  const order = complete ? [...rows].sort((a, b) => b.key - a.key) : [];
  order.forEach((r, i) => {
    r.rank = i + 1;
    r.top3 = i < 3;
  });
  const top = order.slice(0, 3);

  // Step 3 and 4: the tests, the total and the guardrail flag, for the top three only
  top.forEach((r) => {
    r.scal = N(ans, `test.${r.id}.scal`);
    r.risk = S(ans, `test.${r.id}.risk`);
    r.trust = S(ans, `test.${r.id}.trust`);
    r.human = S(ans, `test.${r.id}.human`);
    const screened = !!(r.risk && r.trust && r.human);
    if (r.risk === 'High') r.reasons.push('Risk High');
    if (r.trust === 'minus') r.reasons.push('Trust minus');
    if (r.human === 'minus') r.reasons.push('Human minus');
    r.flag = screened && r.reasons.length > 0;
    r.flagText = screened ? (r.flag ? `Guardrail flag: ${r.reasons.join('; ')}` : 'None') : '';
    r.total = r.scal !== null ? r.value + r.feas + r.scal : null;
  });

  // Step 4: the data check runs for every candidate (Feasibility 4 or 5, and Data and AI at level 2 or lower)
  rows.forEach((r) => {
    r.needs = r.feas !== null && level !== null && r.feas >= 4 && level <= 2;
    if (r.feas === null || level === null) r.need = '';
    else if (!r.needs) r.need = 'Not needed';
    else r.need = r.data ? 'Data named' : 'Name the data';
  });

  // Step 5: Total, then the higher Value, then the higher Feasibility, then the lower use case number
  const picksReady = top.length > 0 && top.every((r) => r.total !== null);
  const pickKey = (r) => r.total * 10000 + r.value * 1000 + r.feas * 100 - r.n;
  const byTotal = picksReady ? [...top].sort((a, b) => pickKey(b) - pickKey(a)) : [];
  if (byTotal[0]) byTotal[0].pick = 'top';
  if (byTotal[1]) byTotal[1].pick = 'second';
  const flagged = byTotal.find((r) => r.flag) || null;
  const anyTest = top.some((r) => r.scal !== null || r.risk || r.trust || r.human);
  const anyScal = top.some((r) => r.scal !== null);
  return { level, rows, complete, order, top, byTotal, flagged, anyTest, anyScal };
}

// the use case as Canvases 4 to 9 read it
const factOf = (r) => ({
  id: r.id,
  n: r.n,
  name: r.name,
  stage: r.stage,
  type: r.type,
  value: r.value,
  feas: r.feas,
  vf: r.vf,
  quadrant: r.quad,
  top3: r.top3,
  scal: r.scal,
  risk: r.risk,
  trust: r.trust,
  human: r.human,
  total: r.total,
  flag: r.flag,
  pick: r.pick,
  vtag: r.vtag,
  data: r.data,
  dataNeeded: r.needs,
  weak: r.weak,
  idea: r.idea,
});

const tileOf = (r) => {
  if (!r) return '';
  const flag = r.flag ? `. ${r.flagText}: Canvas 6 tests it first` : '';
  return `Use case ${r.n}: ${r.name}. ${r.total} of 15, ${r.quad}${flag}`;
};

const rowsFromCands = (up) => candsOf(up).map((c) => ({ id: c.id, label: c.name || `Candidate ${c.n}`, sub: subOf(c) || undefined }));

// ---------------------------------------------------------------- NorthStar (master spec section 6; workbook ns_data.usecases)
const NORTHSTAR = {
  ...T('uc', [
    { value: '4', vtag: 'E', feas: '4' },
    { value: '3', vtag: 'E', feas: '4' },
    { value: '3', vtag: 'E', feas: '2' },
    { value: '5', vtag: 'E', feas: '3' },
    { value: '2', vtag: 'G', feas: '4' },
  ]),
  ...T(
    'test',
    [
      { scal: '4', risk: 'Medium', trust: 'zero', human: 'zero' },
      { scal: '3', risk: 'Medium', trust: 'zero', human: 'zero' },
      { scal: '3', risk: 'High', trust: 'plus', human: 'zero' },
    ],
    ['r1', 'r2', 'r4'],
  ),
  ...T('fin', [
    {
      data: 'Core banking KYC data, in the core banking system, owned by the Core banking lead',
      weak: 'Value: business gain and strategic fit are 4 (customer impact is 5)',
    },
    { data: 'Uploaded documents, in the document store, owned by the Operations lead' },
    { weak: 'Feasibility: approval is needed market by market; impersonation risk' },
    {},
    { data: 'Churn model data; no owner named' },
  ]),
  'so.reviewer': 'IT and Data',
  'so.objection': 'Where does the data for your top pick live, who owns it, and is it clean enough?',
  'so.evidence': 'Core banking KYC data, Core banking lead: Canvas 3 data cell. Quality not yet checked',
  'so.owner': 'Core banking lead',
  'so.status': 'Open',
};

export default makeDef(3, {
  short: 'Use Cases',
  purpose: 'Score your candidates, find the top three, test them, and name your top pick and second pick.',
  howTo: [
    'Score each candidate once on Value and once on Feasibility, from 1 to 5. Each score is set by its weakest part.',
    'The canvas works out V + F, the quadrant and the top three. Then test the top three on Scalability, Risk, Trust and Human.',
    'The total, the guardrail flag, the top pick and the second pick are calculated for you.',
    'Name the data wherever Feasibility is 4 or 5 and Data and AI is level 2 or lower. The flag travels with the use case to Canvases 4 and 6.',
  ],
  results: [
    { id: 'top', label: 'Top pick', rule: 'Highest Total of 15. A tie goes to the higher Value.' },
    { id: 'second', label: 'Second pick', rule: 'Next highest Total. Write its flag, if it has one.' },
    { id: 'flags', label: 'Guardrail flags among the top three', rule: 'Count the guardrail flags in Step 4.' },
    { id: 'scale', label: 'Scale check', rule: 'A Quick Win with Scalability 2 or lower: will it scale?' },
  ],
  steps: [
    {
      hint: J(
        'Score each use case once on Value and once on Feasibility, each set by its weakest part.',
        'Value: customer impact, business gain, strategic fit. Feasibility: data, technology, delivery.',
        'The anchors for 5, 3 and 1 are in the reference below.',
      ),
      blocks: [
        pull('p1', 'From your earlier canvases', [
          {
            id: 'cands',
            label: 'Candidates',
            ref: 'Canvas 2, Step 4',
            no: 2,
            get: (up) => {
              const list = candsOf(up);
              if (!list.length) return null;
              return list.map((c, i) => `${c.n || i + 1}  ${c.name}${subOf(c) ? ` (${subOf(c)})` : ''}`).join('\n');
            },
            note: 'Add or change candidates on Canvas 2.',
          },
          { id: 'level', label: 'Data and AI level', ref: 'Canvas 1', no: 1, get: (up) => levelText(up) },
          {
            id: 'idea',
            label: 'The idea on the Case Card',
            ref: 'Case Card E',
            no: 0,
            get: (up) => {
              const idea = cardOf(up).idea;
              if (!has(idea)) return null;
              const c = candsOf(up).find((x) => x.idea);
              return c ? `${idea}\nCarried by use case ${c.n}: ${c.name}` : `${idea}\nNo candidate on Canvas 2 is marked as carrying it`;
            },
          },
        ]),
        table('uc', {
          title: 'Screen all five',
          rowLabel: 'Use case',
          rows: { from: (up) => rowsFromCands(up) },
          pdf: 'table',
          columns: [
            pick('value', 'Value 1 to 5', SCORE_OPTS, {
              required: true,
              w: 2,
              guide: G(
                'Score Value from 1 to 5, set by its weakest part: customer impact, business gain, strategic fit. 4 and 2 sit between the anchors.',
                '4 for Pre-fill and document reading (customer impact 5, business gain 4, strategic fit 4). 5 for Decline reason and review.',
                anchorRule(VALUE_ANCH),
                'Do not average the three parts. The criterion is as strong as its weakest part.',
              ),
            }),
            pick('vtag', 'Value tag', OPT.tag, {
              required: true,
              w: 1,
              guide: G(
                'Tag the Value score: M if the proof comes from a report or system, E if it is an estimate with a stated basis, G if it is a guess.',
                'E for four use cases; G for Day-14 activation trigger.',
                'A Value score tagged E or G goes to the Evidence Gaps list.',
                'No proof yet? Tag it G. Do not leave it blank.',
              ),
            }),
            pick('feas', 'Feasibility 1 to 5', SCORE_OPTS, {
              required: true,
              w: 2,
              guide: G(
                'Score Feasibility from 1 to 5, set by its weakest part: data, technology, delivery. 4 and 2 sit between the anchors.',
                '4 for Pre-fill and document reading; 2 for Automatic identity verification (approval is needed market by market; impersonation risk).',
                anchorRule(FEAS_ANCH),
                'A 4 or 5 needs the data named in Step 4 when Data and AI is level 2 or lower. Do not score what you hope the data will be.',
              ),
            }),
          ],
        }),
        reference('Anchors for Value: score each criterion as its weakest part', ['Part', 'Score 5', 'Score 3', 'Score 1', 'Proof to bring'], VALUE_ANCH),
        reference('Anchors for Feasibility: score each criterion as its weakest part', ['Part', 'Score 5', 'Score 3', 'Score 1', 'Proof to bring'], FEAS_ANCH),
        reference('Evidence tags', ['Tag', 'Meaning'], TAG_KEY),
      ],
    },
    {
      hint: J(
        'Check where each use case lands. The top three are the three highest V + F; a tie goes to the higher Value.',
        'High is 4 or 5.',
      ),
      blocks: [
        table('plot', {
          title: 'Plot',
          rowLabel: 'Use case',
          rows: { from: (up) => rowsFromCands(up) },
          pdf: 'table',
          columns: [
            calc('value', 'Value', {
              w: 1,
              rule: 'from Step 1',
              guide: G('Worked out for you: the Value score you gave in Step 1.', '4 for Pre-fill and document reading.'),
            }),
            calc('feas', 'Feasibility', {
              w: 1,
              rule: 'from Step 1',
              guide: G('Worked out for you: the Feasibility score you gave in Step 1.', '4 for Pre-fill and document reading.'),
            }),
            calc('vf', 'V + F', {
              w: 1,
              rule: 'add them',
              guide: G('Worked out for you: Value plus Feasibility, out of 10.', '8 for Pre-fill and document reading.'),
            }),
            calc('quad', 'Quadrant', {
              w: 2,
              rule: 'from the plot',
              guide: G(
                'Worked out for you: the quadrant where Value and Feasibility meet.',
                'Quick Win for Pre-fill and document reading.',
                QUADS.map(([name, rule]) => `${name}: ${rule}.`).join(' '),
              ),
            }),
            calc('rank', 'Rank by V + F', {
              w: 1,
              rule: 'highest V + F first; a tie goes to the higher Value',
              guide: G('Worked out for you: rank 1 is the highest V + F. A tie goes to the higher Value.', '2 for Pre-fill and document reading (4 is first).'),
            }),
            calc('top', 'Top three?', {
              w: 1,
              rule: 'Yes for ranks 1 to 3',
              guide: G('Worked out for you: Yes for the three highest V + F.', 'Yes for use cases 4, 1 and 2.'),
            }),
          ],
        }),
        fields([
          calc('rank.1', 'Highest V + F', {
            w: 4,
            rule: 'the three highest V + F; a tie goes to the higher Value',
            guide: G('Worked out for you: the use case with the highest V + F.', 'Use case 4: Decline reason and review (V + F 8, Strategic Bet).'),
          }),
          calc('rank.2', 'Second highest', {
            w: 4,
            rule: 'next highest V + F',
            guide: G('Worked out for you: the use case with the next highest V + F.', 'Use case 1: Pre-fill and document reading (V + F 8, Quick Win).'),
          }),
          calc('rank.3', 'Third highest', {
            w: 4,
            rule: 'next highest V + F',
            guide: G('Worked out for you: the third use case to test.', 'Use case 2: Guided upload assistant (V + F 7, Fill-in).'),
          }),
          calc('quad.bet', 'Strategic Bet', {
            w: 2,
            rule: 'Value 4 or 5, Feasibility 1 to 3',
            guide: G('Worked out for you: the use cases in this quadrant. The top three are marked.', 'Use case 4 (top three).'),
          }),
          calc('quad.win', 'Quick Win', {
            w: 2,
            rule: 'Value 4 or 5, Feasibility 4 or 5',
            guide: G('Worked out for you: the use cases in this quadrant. The top three are marked.', 'Use case 1 (top three).'),
          }),
          calc('quad.rec', 'Reconsider', {
            w: 2,
            rule: 'Value 1 to 3, Feasibility 1 to 3',
            guide: G('Worked out for you: the use cases in this quadrant. The top three are marked.', 'Use case 3.'),
          }),
          calc('quad.fill', 'Fill-in', {
            w: 2,
            rule: 'Value 1 to 3, Feasibility 4 or 5',
            guide: G('Worked out for you: the use cases in this quadrant. The top three are marked.', 'Use case 2 (top three); use case 5.'),
          }),
        ]),
        reference('The four quadrants', ['Quadrant', 'Rule'], QUADS),
      ],
    },
    {
      hint: J(
        'Test the top three. Scalability is the weakest of reach and change.',
        'Risk, Trust and Human are screens: their meanings are in the reference below.',
      ),
      blocks: [
        note('The top three appear here once every use case in Step 1 has a Value and a Feasibility score.'),
        table('test', {
          title: 'Test the top three',
          rowLabel: 'Use case',
          rows: {
            from: (up, v) =>
              analyse(v, up)
                .top.slice()
                .sort((a, b) => a.n - b.n)
                .map((r) => ({ id: r.id, label: r.name, sub: subOf(r) || undefined })),
          },
          pdf: 'table',
          columns: [
            pick('scal', 'Scalability 1 to 5', SCORE_OPTS, {
              required: true,
              w: 2,
              guide: G(
                'Score Scalability from 1 to 5, set by the weakest of reach and change. 4 and 2 sit between the anchors.',
                '4 for Pre-fill and document reading; 3 for Guided upload assistant and for Decline reason and review.',
                anchorRule(SCAL_ANCH),
                'Scalability is not Value. Ask whether the same data and model could serve a second journey, and whether the frontline would change.',
              ),
            }),
            pick('risk', 'Risk', OPT.risk, {
              required: true,
              w: 2,
              guide: G(
                'Pick the risk of the use case: Low, Medium or High.',
                'Medium for Pre-fill and document reading (it uses identity data); High for Decline reason and review.',
                screenRule(0),
                'Risk High is not a reason to drop a use case. It is a reason to test it first on Canvas 6.',
              ),
            }),
            pick('trust', 'Trust', OPT.pzm, {
              required: true,
              w: 2,
              guide: G(
                'Pick plus, zero or minus, at the trust moments you marked on Canvas 2.',
                'zero for Pre-fill and document reading; plus for Decline reason and review.',
                screenRule(1),
                'Trust minus makes a guardrail flag. Check the trust moments on Canvas 2 before you pick.',
              ),
            }),
            pick('human', 'Human', OPT.pzm, {
              required: true,
              w: 2,
              guide: G(
                'Pick plus, zero or minus, for what the use case does to the work of the people involved.',
                'zero for all three NorthStar use cases tested.',
                screenRule(2),
                'Human minus makes a guardrail flag. Do not pick zero because nobody has asked the frontline.',
              ),
            }),
          ],
        }),
        reference('Anchors for Scalability: score as the weakest of two parts', ['Part', 'Score 5', 'Score 3', 'Score 1'], SCAL_ANCH),
        reference('The three screens: Risk, Trust and Human', ['Screen', 'Low or plus', 'Medium or zero', 'High or minus'], SCREENS),
      ],
    },
    {
      hint: J(
        'Read the total and the guardrail flag, then name the data.',
        'Guardrail flag = Risk High, or Trust minus, or Human minus (AICX working rule).',
        'Name the data when Feasibility is 4 or 5 and Data and AI is level 2 or lower.',
      ),
      blocks: [
        pull('p4', 'From Canvas 1', [{ id: 'level', label: 'Data and AI level', ref: 'Canvas 1', no: 1, get: (up) => levelText(up) }]),
        note(FLAG_KEY, 'rule'),
        table('fin', {
          title: 'Total, flag and data',
          rowLabel: 'Use case',
          rows: { from: (up) => rowsFromCands(up) },
          pdf: 'cards',
          columns: [
            calc('total', 'Total of 15', {
              w: 1,
              rule: 'V + F + S, for the top three',
              guide: G('Worked out for you: Value plus Feasibility plus Scalability, for the top three.', '12 for Pre-fill and document reading (4 + 4 + 4).'),
            }),
            calc('flag', 'Guardrail flag', {
              w: 2,
              rule: 'Risk High, or Trust minus, or Human minus',
              guide: G(
                'Worked out for you: None, or the reasons for the guardrail flag.',
                'Guardrail flag: Risk High, for Decline reason and review.',
                'The flag travels with the use case to Canvases 4 and 6.',
              ),
            }),
            calc('need', 'Name the data?', {
              w: 1,
              rule: 'needed when Feasibility is 4 or 5 and Data and AI is level 2 or lower',
              guide: G('Worked out for you: whether this use case needs its data named.', 'Data named for use cases 1, 2 and 5. Not needed for use cases 3 and 4.'),
            }),
            text('data', 'Which data, in which system, owned by whom', {
              w: 4,
              placeholder: 'The data, the system, the owner',
              guide: G(
                'Name the data the use case needs, the system it sits in, and who owns it. Do it for every use case that asks.',
                'Core banking KYC data, in the core banking system, owned by the Core banking lead',
                'Needed when Feasibility is 4 or 5 and Data and AI is level 2 or lower.',
                '"No owner named" is an honest answer and a finding. A system name alone is not enough: name the owner.',
              ),
            }),
            text('weak', 'Weakest part (optional)', {
              w: 4,
              placeholder: 'Which part set the score, and why',
              guide: G(
                'Optional. Say which part set a score: customer impact, business gain, strategic fit, data, technology, delivery, reach or change.',
                'Value: business gain and strategic fit are 4 (customer impact is 5)',
                undefined,
                'Say which part set the score. Do not write a second score.',
              ),
            }),
          ],
        }),
      ],
    },
    {
      hint: J(
        'Name your top pick and second pick.',
        'The top pick has the highest Total of 15; a tie goes to the higher Value.',
        'The second pick has the next highest Total. Write its flag, if it has one.',
      ),
      blocks: [
        fields([
          calc('pick.top', 'Top pick', {
            w: 2,
            rule: 'highest Total of 15; a tie goes to the higher Value',
            guide: G(
              'Worked out for you: the use case with the highest Total of 15. Canvas 4 values it.',
              'Use case 1: Pre-fill and document reading. 12 of 15, Quick Win',
              'A tie goes to the higher Value.',
            ),
          }),
          calc('pick.second', 'Second pick', {
            w: 2,
            rule: 'next highest Total; its flag, if it has one',
            guide: G(
              'Worked out for you: the use case with the next highest Total. If it carries a guardrail flag, Canvas 6 tests it first.',
              'Use case 4: Decline reason and review. 11 of 15, Strategic Bet. Guardrail flag: Risk High: Canvas 6 tests it first',
            ),
          }),
          calc('pick.flags', 'Guardrail flags among the top three', {
            w: 1,
            rule: 'count the guardrail flags in Step 4',
            guide: G('Worked out for you: how many of the top three carry a guardrail flag.', '1'),
          }),
          calc('pick.scale', 'Scale check', {
            w: 3,
            rule: 'a Quick Win with Scalability 2 or lower: will it scale?',
            guide: G(
              'Worked out for you: a warning if a Quick Win scores 2 or lower on Scalability.',
              'No Quick Win scores 2 or lower on Scalability',
              undefined,
              'A quick win that cannot scale is a one-off. Say so in your pitch.',
            ),
          }),
        ]),
      ],
    },
  ],
  derive(v, up) {
    const a = analyse(v, up);
    const calcOut = {};
    a.rows.forEach((r) => {
      calcOut[`plot.${r.id}.value`] = r.value === null ? '' : String(r.value);
      calcOut[`plot.${r.id}.feas`] = r.feas === null ? '' : String(r.feas);
      calcOut[`plot.${r.id}.vf`] = r.vf === null ? '' : String(r.vf);
      calcOut[`plot.${r.id}.quad`] = r.quad;
      calcOut[`plot.${r.id}.rank`] = r.rank === null ? '' : String(r.rank);
      calcOut[`plot.${r.id}.top`] = r.rank === null ? '' : r.top3 ? 'Yes' : 'No';
      calcOut[`fin.${r.id}.total`] = r.total === null ? '' : String(r.total);
      calcOut[`fin.${r.id}.flag`] = r.flagText;
      calcOut[`fin.${r.id}.need`] = r.need;
    });
    const line = (r) => (r ? `Use case ${r.n}: ${r.name} (V + F ${r.vf}, ${r.quad})` : '');
    calcOut['rank.1'] = line(a.order[0]);
    calcOut['rank.2'] = line(a.order[1]);
    calcOut['rank.3'] = line(a.order[2]);
    const inQuad = (q) =>
      a.rows
        .filter((r) => r.quad === q)
        .map((r) => `Use case ${r.n}${r.top3 ? ' (top three)' : ''}`)
        .join('; ');
    calcOut['quad.bet'] = inQuad('Strategic Bet');
    calcOut['quad.win'] = inQuad('Quick Win');
    calcOut['quad.rec'] = inQuad('Reconsider');
    calcOut['quad.fill'] = inQuad('Fill-in');

    const topPick = a.byTotal[0] || null;
    const secondPick = a.byTotal[1] || null;
    const slow = a.top
      .filter((r) => r.quad === 'Quick Win' && r.scal !== null && r.scal <= 2)
      .sort((x, y) => x.n - y.n)
      .map((r) => `Use case ${r.n} is quick, but will it scale?`);
    let scale = '';
    if (a.anyScal) scale = slow.length ? slow.join(' ') : 'No Quick Win scores 2 or lower on Scalability';
    const results = {
      top: tileOf(topPick),
      second: tileOf(secondPick),
      flags: a.anyTest ? String(a.top.filter((r) => r.flag).length) : '',
      scale,
    };
    calcOut['pick.top'] = results.top;
    calcOut['pick.second'] = results.second;
    calcOut['pick.flags'] = results.flags;
    calcOut['pick.scale'] = results.scale;

    const evidence = [];
    a.rows.forEach((r) => {
      if (r.vtag === 'E' || r.vtag === 'G') {
        const score = r.value !== null ? ` (score ${r.value})` : '';
        evidence.push(gap(`Value of ${r.name}${score}`, r.vtag, `Canvas 3, Value, use case ${r.n}`));
      }
    });

    const warnings = [];
    if (!a.rows.length) {
      warnings.push({ step: 's1', text: 'Canvas 2 has no candidates yet. Number up to five there, then come back.' });
    } else if (a.rows.length < 3) {
      warnings.push({ step: 's1', text: 'Canvas 2 lists fewer than three candidates, so there is no top three to test. Add more on Canvas 2.' });
    }
    if (a.level === null && a.rows.some((r) => r.feas !== null && r.feas >= 4)) {
      warnings.push({ step: 's4', text: 'Canvas 1 has no Data and AI level yet, so the data check cannot run.' });
    }
    a.rows.forEach((r) => {
      if (r.need === 'Name the data') {
        const why = `Feasibility is ${r.feas} and Data and AI is level ${a.level}.`;
        warnings.push({ step: 's4', text: `Use case ${r.n}: ${why} Name the data, the system and the owner.` });
      }
    });

    return {
      calc: calcOut,
      results,
      evidence,
      warnings,
      blockers: [],
      facts: {
        candidates: a.rows.map(factOf),
        top3: a.order.slice(0, 3).map((r) => r.id),
        topPick: topPick ? factOf(topPick) : null,
        secondPick: secondPick ? factOf(secondPick) : null,
        flagged: a.flagged ? factOf(a.flagged) : null,
        dataAiLevel: a.level,
      },
    };
  },
  NORTHSTAR,
});
